import React, {useEffect} from "react";
import {useWindowDimensionUpdate} from "../../hooks/window";
import {useScreenBox, useZoomTranslate} from "./ZoomTranslate";
import store from "../../store";
import {getAngle} from "../../common/math";
import {_scale} from "../nodes";
import {isExecutableNode} from "../../store/nodes";

interface Ray {
    angle: number;
    dir: {x: number; y: number};
}

function createRays(x: number, y: number, length: number): Ray[] {
    const numRays = length;
    let rays = new Array<Ray>(numRays + 1);

    for (let i = 0; i <= numRays; i++) {
        const angle = (i / numRays) * Math.PI / 2; // Calculate angle
        const dirX = Math.cos(angle); // Calculate direction x-component
        const dirY = Math.sin(angle); // Calculate direction y-component
        rays[i] = {angle, dir: {x: dirX, y: dirY}}; // Push ray to array
    }

    return rays;
}

var quarterRayCount = 90;
var rayCount = quarterRayCount * 4;
var quarterRays = createRays(0, 0, quarterRayCount);

function intersectRayWithLine(ray: Ray, x1: number, y1: number, x2: number, y2: number): {x: number; y: number} {
    const {x: rayX, y: rayY} = ray.dir;

    const dx = x2 - x1;
    const dy = y2 - y1;

    const t1 = (dx * y1 - dy * x1) / (rayX * dy - rayY * dx);
    const t2 = (rayX * (0 - x1) - rayY * (0 - y1)) / (dx * rayY - dy * rayX);

    const x = rayX * t1;
    const y = rayY * t1;
    return {x, y};
}

function quarterBoxIntersection(rays: Ray[], width: number, height: number): {x: number; y: number}[][] {
    let overTheEdge = false;
    return rays.map<{x: number; y: number}[]>((rayi, i, a) => {
        if (i === a.length - 1) return [];
        let j = i + 1;
        let rayj = a[j];

        if (!overTheEdge) {
            let ii = intersectRayWithLine(rayi, width, 0, width, height);
            let ij = intersectRayWithLine(rayj, width, 0, width, height);
            if (ii?.y < height && ij?.y <= height && ii?.y > -height && ij?.y >= -height) {
                return [ii, ij];
            } else {
                overTheEdge = true;
                ij = intersectRayWithLine(rayj, 0, height, width, height);
                return [ii, {x: -width, y: -height}, ij];
            }
        } else {
            let ii = intersectRayWithLine(rayi, 0, height, width, height);
            let ij = intersectRayWithLine(rayj, 0, height, width, height);
            return [ii, ij];
        }
    }).filter(r => r.length > 0);
}

const FancyIndicator = ({strokeWidth}: {strokeWidth?: number}) => {
    useWindowDimensionUpdate();
    let box = useScreenBox();
    const [values, setValues] = React.useState<number[]>([]);

    let width = window.innerWidth;
    let height = window.innerHeight;
    let sw = strokeWidth ?? 4;
    let wo = width - sw * 2;
    let ho = height - sw * 2;
    let wh = width / 2;
    let hh = height / 2;
    let woh = wo / 2;
    let hoh = ho / 2;

    useEffect(() => {
        let sbx = box.x + box.width / 2;
        let sby = box.y + box.height / 2;
        let a = Array.from(Array<number>(rayCount), () => 0);
        let nodes = store.getState().nodes.nodes;

        let method: 'spread' | 'smooth' | 'none' = true ? 'smooth' : 'spread';

        Object.values(nodes).forEach(node => {
            if (isExecutableNode(node) && (
                (node.shape.x + node.shape.width) * _scale < box.x ||
                (node.shape.y + node.shape.height) * _scale < box.y ||
                node.shape.x * _scale > box.x + box.width ||
                node.shape.y * _scale > box.y + box.height)
            ) {
                let edges = [
                    {
                        x: node.shape.x * _scale,
                        y: node.shape.y * _scale
                    },
                    {
                        x: (node.shape.x + node.shape.width) * _scale,
                        y: node.shape.y * _scale
                    },
                    {
                        x: (node.shape.x + node.shape.width) * _scale,
                        y: (node.shape.y + node.shape.height) * _scale
                    },
                    {
                        x: node.shape.x * _scale,
                        y: (node.shape.y + node.shape.height) * _scale
                    },
                ];

                let edgeResults = edges.map(e => {
                    let dx = e.x - sbx;
                    let dy = e.y - sby;
                    let angle = getAngle(-dx, -dy);
                    let ii = angle / (Math.PI / 2) * quarterRayCount;
                    let i = Math.floor(ii);
                    let f = ii - i;
                    return [angle, i, f];
                });

                const indices = edgeResults.map(e => e[1]);
                const onZero = Math.abs(Math.max(...indices) - Math.min(...indices)) > rayCount / 2;
                if (onZero) {
                    edgeResults.forEach(er => {
                        if (er[1] > rayCount / 2) {
                            er[1] -= rayCount;
                        }
                    })
                }
                edgeResults.sort((a, b) => a[0] - b[0]);

                /*let angle = getAngle(-dx, -dy);
                let ii = angle / (Math.PI / 2) * quarterRayCount;
                let i = Math.floor(ii);
                let f = ii - i;*/

                let [, minI, minF] = edgeResults[0];
                let [, maxI, maxF] = edgeResults[3];

                if (method === 'spread') {
                    [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6].forEach(iii => {
                        let f = iii < 0 ? minF : maxF;
                        let i = iii < 0 ? minI : maxI;
                        let d = Math.abs(iii - f) / 6;
                        let v = 1 - d;

                        const set = (i_: number, vv?: number) => {
                            let iiii = iii + i_;
                            if (iiii < 0) iiii += rayCount;
                            if (iiii >= rayCount) iiii -= rayCount;
                            a[iiii] += (vv ?? v) * .5;
                        }

                        if (iii === 0) {
                            for (let i_ = minI; i_ <= maxI; i_++) {
                                set(i_, i_ === minI ? 1 - minF / 4 : 1);
                            }
                        } else {
                            set(i);
                        }
                    });

                } else {
                    const set = (i_: number, vv: number) => {
                        let iiii = i_;
                        if (iiii < 0) iiii += rayCount;
                        if (iiii >= rayCount) iiii -= rayCount;
                        a[iiii] += vv * .5;
                    }

                    for (let i_ = minI; i_ <= maxI; i_++) {
                        set(i_, i_ === minI ? 1 - minF : (i_ === maxI ? maxF : 1));
                    }
                }
            }
        });

        if (method === 'smooth') {
            let arr = [.2, .15, .1, .075, .05, .025]; //[.3, .2, .1, .05];

            let a_ = Array.from(Array<number>(rayCount), () => 0);
            for (let i = 0; i < a.length; i++) {
                let v = a[i];
                for (let iii = -5; iii <= 5; iii++) {
                    let w = arr[Math.abs(iii)];
                    let i_ = i + iii;
                    if (i_ < 0) i_ += rayCount;
                    if (i_ >= rayCount) i_ -= rayCount;
                    a_[i_] += v * w;
                }
            }

            a = a_;
        }

        setValues(a);

    }, [box]);

    let qbi = quarterBoxIntersection(quarterRays, woh, hoh);
    let qbis = qbi.map((r, i, a) => {
        return [...r, ...[...r].reverse().map(p => ({x: 1.04 * p.x, y: 1.04 * p.y}))]
    });

    return <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none"
    }}>
        <svg style={{position: 'absolute', left: 0, top: 0, width, height}} fillOpacity={.3333}>
            {/*<defs>
                {defsAndRects.map(d => d[0])}
            </defs>*/}
            {values.map((v, i, a) => {
                if (!v) return null;
                let vv = Math.pow(v / 2, .5);
                if (vv > 1) vv = 1;

                if (i < quarterRayCount) {
                    let r = qbis[i];
                    return <polygon points={r.map(p => `${wh + p.x},${hh + p.y}`).join(" ")} fill={`rgba(0, 64, 128, ${vv})`}/>;
                } else if (i < 2 * quarterRayCount) {
                    let r = qbis[2 * quarterRayCount - 1 - i];
                    return <polygon points={r.map(p => `${wh - p.x},${hh + p.y}`).join(" ")} fill={`rgba(0, 64, 128, ${vv})`}/>
                } else if (i < 3 * quarterRayCount) {
                    let r = qbis[i - 2 * quarterRayCount];
                    return <polygon points={r.map(p => `${wh - p.x},${hh - p.y}`).join(" ")} fill={`rgba(0, 64, 128, ${vv})`}/>
                } else {
                    let r = qbis[rayCount - 1 - i];
                    return <polygon points={r.map(p => `${wh + p.x},${hh - p.y}`).join(" ")} fill={`rgba(0, 64, 128, ${vv})`}/>
                }
            })}
        </svg>
    </div>
}

export default FancyIndicator;